import React from "react";
import { useLang } from "../contexts/LanguageContext";
import {
  Gem, Gift, Coffee, Heart, Infinity, Download, BarChart2, Sparkles
} from "lucide-react";

// Multi-language text & feature list
const t = {
  en: {
    title: "Spendora Premium",
    subtitle: "Unlock all premium features and use Spendora at full power.",
    features: [
      { icon: <Infinity size={20} className="text-blue-500 mr-2" />, text: "Unlimited activity history" },
      { icon: <Download size={20} className="text-green-500 mr-2" />, text: "Export data as CSV" },
      { icon: <BarChart2 size={20} className="text-blue-500 mr-2" />, text: "View advanced Insights & analytics" },
      { icon: <Sparkles size={20} className="text-yellow-400 mr-2" />, text: "Other special functions" },
    ],
    monthly: "Spendora Monthly",
    annual: "Spendora Annual (2 months FREE!)",
    subscribeNow: "Subscribe Now",
    support: "Support Spendora",
    supportDesc: "Help with server and app costs—anything you can give makes a difference!",
    coffee: "Buy Me a Coffee",
    thanks: "Thank you for your support!",
    note1: "(Just so you know, this isn’t associated with Premium features.)",
    activated: "* Premium is activated instantly after subscribing",
    note: "(If your Premium status doesn't show up, try Refresh or Login again)",
    perMonth: "49 THB / month",
    perYear: "490 THB / year",
    bestValue: "Best value! One-time payment for 1 year Premium",
    cancelAnytime: "Cancel anytime",
  },
  th: {
    title: "Spendora Premium",
    subtitle: "ปลดล็อกฟีเจอร์พิเศษและใช้แอปได้เต็มศักยภาพ",
    features: [
      { icon: <Infinity size={20} className="text-blue-500 mr-2" />, text: "บันทึกกิจกรรมได้ไม่จำกัด" },
      { icon: <Download size={20} className="text-green-500 mr-2" />, text: "ส่งออกข้อมูล CSV" },
      { icon: <BarChart2 size={20} className="text-blue-500 mr-2" />, text: "ดูสถิติ/อินไซต์กิจกรรมขั้นสูง" },
      { icon: <Sparkles size={20} className="text-yellow-400 mr-2" />, text: "ฟังก์ชันพิเศษอื่นๆ" },
    ],
    monthly: "Spendora รายเดือน",
    annual: "Spendora รายปี (ฟรี 2 เดือน!)",
    subscribeNow: "สมัครสมาชิก Premium",
    support: "สนับสนุน Spendora",
    supportDesc: "ช่วยสนับสนุนค่าเซิร์ฟเวอร์/ค่าใช้จ่ายให้แอปอยู่ต่อได้ เลือกจำนวนเท่าไหร่ก็ได้",
    coffee: "Buy Me a Coffee",
    thanks: "ขอบคุณที่สนับสนุน!",
    note1: "(ไม่ผูกกับฟีเจอร์ Premium)",
    activated: "* รับสิทธิ์ Premium ทันทีหลังสมัครสมาชิก",
    note: "(ถ้าไม่ขึ้นสถานะ Premium ให้ลอง Refresh หรือ Login ใหม่)",
    perMonth: "49 บาท / เดือน",
    perYear: "490 บาท / ปี",
    bestValue: "คุ้มสุด! จ่ายครั้งเดียว ใช้งาน Premium 1 ปีเต็ม",
    cancelAnytime: "ยกเลิกเมื่อไหร่ก็ได้",
  },
};

export default function Subscribe({ userProfile }) {
  const { lang } = useLang();
  const locale = t[lang] || t.en;
  const isPremium = userProfile?.is_premium;

  return (
    <div className="max-w-lg mx-auto py-8 px-2 sm:px-0">
      {/* Premium Card */}
      <div className="bg-white rounded-3xl shadow-lg px-6 py-8 flex flex-col items-center mb-10">
        <Gem className="text-indigo-400 mb-1" size={36} />
        <div className="flex items-center gap-2 text-2xl sm:text-3xl font-bold mt-2 mb-2">
          <span className="text-yellow-400"><Gift size={28} /></span>
          <span>{locale.title}</span>
        </div>
        <div className="text-gray-700 mb-4 text-center">{locale.subtitle}</div>
        <div className="w-full flex flex-col gap-2 mb-4">
          <div className="font-semibold text-base text-gray-800 text-left mb-1">
            {lang === "th" ? "ฟีเจอร์ Premium" : "Premium Features"}
          </div>
          <ul>
            {locale.features.map((f, i) => (
              <li key={i} className="flex items-center py-0.5 pl-1">{f.icon}{f.text}</li>
            ))}
          </ul>
        </div>

        {/* Plan Buttons */}
        <div className="w-full flex flex-col gap-4 mt-4">
          {isPremium ? (
            <div className="block w-full bg-green-50 text-green-600 rounded-2xl py-6 font-bold text-xl text-center shadow border border-green-300 my-2">
              ✅ Premium Activated
              <div className="text-xs text-gray-500 mt-2">{locale.activated}</div>
            </div>
          ) : (
            <>
              {/* Monthly */}
              <a
                href="https://buy.stripe.com/fZudRa3sO3pC3jS1rc6AM00"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-blue-100 hover:bg-blue-200 rounded-2xl py-4 text-blue-900 font-semibold text-lg text-center shadow transition border border-blue-300"
              >
                <Gift className="inline-block mr-2 -mt-1" size={20} />
                {locale.monthly} <span className="font-normal">• {locale.perMonth}</span>
                <div className="text-xs mt-1 text-blue-500/80">{locale.cancelAnytime}</div>
              </a>
              {/* Annual */}
              <a
                href="https://buy.stripe.com/8x29AU0gCe4gbQo5Hs6AM01"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-blue-500 hover:bg-blue-600 rounded-2xl py-5 text-white font-bold text-lg text-center shadow transition border-2 border-blue-700"
                style={{ boxShadow: "0 4px 24px 0 rgba(72, 134, 255, 0.16)" }}
              >
                <Gift className="inline-block mr-2 -mt-1" size={20} />
                {locale.annual} <span className="font-normal">• {locale.perYear}</span>
                <div className="text-xs mt-1 text-blue-200">{locale.bestValue}</div>
              </a>
            </>
          )}
        </div>

        <div className="w-full text-center mt-5 text-xs text-gray-500">
          <Heart className="inline-block text-pink-400 mr-1 mb-0.5" size={14} />
          {locale.activated} <br /> {locale.note}
        </div>
      </div>

      {/* Support Box */}
      <div className="bg-blue-50 rounded-2xl shadow flex flex-col items-center px-6 py-6 mb-10">
        <div className="flex items-center gap-2 text-lg font-bold text-blue-800 mb-2">
          <Coffee className="text-yellow-700" size={20} />
          {locale.support}
        </div>
        <div className="text-gray-700 mb-3 text-center">{locale.supportDesc}</div>
        <a
          href="https://buymeacoffee.com/spendora?status=1"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-yellow-400 hover:bg-yellow-500 text-white font-bold px-6 py-2 rounded-xl shadow flex items-center gap-2"
        >
          <Coffee size={18} /> {locale.coffee}
        </a>
        <span className="text-xs text-gray-400 text-center mt-2">{locale.thanks}<br />{locale.note1}</span>
      </div>
    </div>
  );
}